import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Modal from '@deporvillage/modal-component';
import RegisterPopup from '@deporvillage/register-popup-component';
import ForgotPasswordPopup from '@deporvillage/forgot-password-popup-component';
import Picture from '@deporvillage/picture-component';
import { LoginForm, GoogleSocialLogin } from '@deporvillage/login-component';
import { I18n, Config, getCookie, getCustomerValues, setCustomerValues, getInputError, getFormErrors } from '@deporvillage/utils-library-component';
import Customer from './utils/customer';

import styles from './LoginPopup.module.scss';

const Events = {
    ButtonLogin: 'login-button-login',
    LinkSignUp: 'login-popup-link-sign-up',
    LinkForgotPassword: 'login-link-forgot',
    InputEmail: 'login-popup-input-email',
    InputPassword: 'login-popup-input-password',
    GoogleSocialButtonLogin: 'google-social-button-login',
};

const LoginPopupExtended = ({ onLoginClick, onLoginChange, onClose, redirect }) => {
    const [openLogin, setOpenLogin] = React.useState(true);
    const [openRegister, setOpenRegister] = React.useState(false);
    const [openForgotPassword, setOpenForgotPassword] = React.useState(false);

    const toggleOpenLogin = () => {
        setOpenLogin(!openLogin);
        onClose && onClose();
    };
    const toggleOpenRegister = () => {
        setOpenRegister(!openRegister);
        onClose && onClose();
    };
    const toggleOpenForgotPassword = () => {
        setOpenForgotPassword(!openForgotPassword);
        onClose && onClose();
    };

    const handleClickPopUp = (e) => {
        onLoginClick && onLoginClick(e);
        /* istanbul ignore next */
        switch (e) {
            case Events.LinkSignUp:
                setOpenRegister(!openRegister);
                setOpenLogin(!openLogin);
                break;
            case Events.LinkForgotPassword:
                setOpenForgotPassword(!openRegister);
                setOpenLogin(!openLogin);
                break;
        }
    };
    const handleClickOnRegisterBack = () => {
        setOpenRegister(!openRegister);
        setOpenLogin(!openLogin);
    };

    const handleClickOnForgotBack = () => {
        setOpenForgotPassword(!openForgotPassword);
        setOpenLogin(!openLogin);
    };

    const handleOnLoginChange = (e) => onLoginChange && onLoginChange(e);

    return (
        <div className={styles['login-popup-extended']}>
            {openLogin && (
                <LoginPopup
                    onClose={toggleOpenLogin}
                    onClick={handleClickPopUp}
                    redirect={redirect}
                    onChange={handleOnLoginChange}
                />
            )}
            <RegisterPopup
                onClose={toggleOpenRegister}
                isOpen={openRegister}
                onBack={handleClickOnRegisterBack}
            />
            <ForgotPasswordPopup
                onClose={toggleOpenForgotPassword}
                isOpen={openForgotPassword}
                onBack={handleClickOnForgotBack}
            />
        </div>
    );
};

LoginPopupExtended.propTypes = {
    onLoginChange: PropTypes.func,
    onLoginClick: PropTypes.func,
    onClose: PropTypes.func,
    redirect: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
};

const LoginPopup = ({ onChange, onClick, onClose, redirect }) => {
    const [customerData, setCustomerData] = React.useState(setCustomerValues(Customer));
    const [loginError, setLoginError] = React.useState(null);
    const [loading, setLoading] = useState(false);

    const { api, country } = React.useContext(Config);

    // istanbul ignore next
    const postCustomerLogin = async (api, payload) => {
        const tokenMd5 = getCookie('token_md5');
        return fetch(`${api}/api/customer/login`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                country: country.code,
                'dpv-session': getCookie('dpv-session'),
                ...(!tokenMd5 ? {} : { 'token-app': tokenMd5 }),
            },
            credentials: 'include',
            body: JSON.stringify(payload),
        })
            .then((ans) => ans)
            .catch((e) => console.log(e));
    };

    // istanbul ignore next
    const handleOnLogin = async (form, e) => {
        onClick && onClick(e);
        const [result, hasErrors] = getFormErrors(form);
        if (hasErrors) {
            setCustomerData(result);
        } else {
            setLoading(true);
            try {
                const res = await postCustomerLogin(api, getCustomerValues(customerData));
                /* eslint-disable no-undef */
                if (res.status === 200) {
                    !redirect || typeof redirect !== 'string'
                        ? window.location.reload()
                        : (window.location.href = redirect);
                } else {
                    const error = await res.json();
                    setLoading(false);
                    setLoginError(error);
                }
            } catch (e) {
                return e;
            }
        }
    };

    // istanbul ignore next
    const handleOnInputChange = (input, form) => {
        const validateInput = {
            ...form,
            [input.id]: {
                ...input,
                error: getInputError(input, form),
            },
        };
        setCustomerData(validateInput);
        setLoginError(null);
    };

    const handleOnClose = () => {
        onClose && onClose();
    };

    const handleOnClickSignUp = (e) => {
        e.preventDefault();
        onClick && onClick(Events.LinkSignUp);
    };

    const handleOnClickSocialLogin = (e) => {
        onClick && onClick(e);
    };

    const handleOnInputFocus = (e) => {
        onChange && onChange(e);
    };

    const handleOnLoginClick = (e) => {
        onClick && onClick(e);
    };

    return (
        <div className={styles['login-popup-component']}>
            <Modal onClose={handleOnClose} className={styles['modal-close']}>
                <div className={`${loading ? styles['loading'] : ''}`}>
                    <div className={styles['login-form-wrapper']}>
                        <div className={styles['login-form-component-wrapper']}>
                            <div className={styles['logo']}>
                                <a href='/' title='Deporvillage'>
                                    <Picture
                                        src={'/static/default/deporvillage_logo.png'}
                                        height={86}
                                        lazy
                                    />
                                </a>
                            </div>
                            <LoginForm
                                customerData={customerData}
                                onLoginClick={handleOnLoginClick}
                                onChange={handleOnInputChange}
                                onFocus={handleOnInputFocus}
                                onLogin={handleOnLogin}
                                error={loginError}
                            />
                        </div>
                    </div>
                    <div className={styles['social-login-wrapper']}>
                        <p className={styles['title-social']}>
                            <I18n>header.login_from.social_login_or</I18n>
                        </p>
                        <GoogleSocialLogin onSocialLoginClick={handleOnClickSocialLogin} />
                        <p className={styles['new-user-title']}>
                            <I18n>header.login_from.new_user_title</I18n>{' '}
                            <strong className={styles['register']} onClick={handleOnClickSignUp}>
                                <I18n>header.login_from.sign_up_here</I18n>
                            </strong>
                        </p>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

LoginPopup.propTypes = {
    onChange: PropTypes.func,
    onClick: PropTypes.func,
    onClose: PropTypes.func,
    redirect: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
};

export default LoginPopup;
export { LoginPopupExtended, Events as LoginPopupEvents };
